import {
  LINE_MESSAGE_TYPES,
  createFlexMessage,
  normalizeMessage,
  validators,
} from "./schemas";

export const ensureHttps = (url) => {
  if (!url) return null;
  if (url.startsWith("https://")) return url;
  if (url.startsWith("http://")) return url.replace("http://", "https://");
  return null;
};

const sanitizeHttpUrl = (url, maxLen = 1000) => {
  if (!url) return null;
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  if (trimmed.length > maxLen) return null;
  return ensureHttps(trimmed);
};

const buildAspectRatio = (meta) => {
  const fallback = { w: 375, h: 220 };
  const w = Number(meta?.width) || null;
  const h = Number(meta?.height) || null;
  if (!w || !h || w <= 0 || h <= 0) return `${fallback.w}:${fallback.h}`;
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const g = gcd(Math.round(w), Math.round(h));
  const rw = Math.max(1, Math.round(w / g));
  const rh = Math.max(1, Math.round(h / g));
  // LINE 接受「寬:高」字串，限制在 1~2000 之間較安全
  const clamp = (n) => Math.min(2000, Math.max(1, n));
  return `${clamp(rw)}:${clamp(rh)}`;
};

const fontSizeToLineSize = (px) => {
  const n = Number(px);
  if (Number.isNaN(n)) return "md";
  if (n <= 14) return "sm";
  if (n <= 16) return "md";
  if (n <= 18) return "lg";
  if (n <= 22) return "xl";
  if (n <= 26) return "xxl";
  return "3xl";
};

const mapButtonToFlex = (btn, { includePreviewMeta = false, layoutStyle } = {}) => {
  if (!btn) return null;
  const text = btn.text || " ";
  const uri = sanitizeHttpUrl(btn.linkUrl);
  const backgroundColor = btn.bgColor || "#000000";
  const textColor = btn.textColor || "#ffffff";
  const action =
    uri && /^https?:\/\//i.test(uri)
      ? { type: "uri", label: text, uri }
      : { type: "message", label: text, text };

  return {
    type: "button",
    action,
    style: "primary",
    color: backgroundColor,
    height: "sm",
    ...(layoutStyle === "horizontal" ? { flex: 1 } : {}),
    // 只有預覽需要自行渲染文字顏色，送出時不帶這個自訂欄位以確保格式正確
    ...(includePreviewMeta ? { textColor } : {}),
  };
};

export const buildFlexBubbleFromPage = (page, options = {}) => {
  const imageUrl =
    ensureHttps(page.imageSrc) || (options.allowLocalImage ? page.imageSrc : null);
  const hasValidImage = Boolean(imageUrl);
  const aspectRatio = buildAspectRatio(page.imageMeta);
  const btnsRaw = (page.buttons || [])
    .map((b) => mapButtonToFlex(b, { ...options, layoutStyle: page.layoutStyle }))
    .filter(Boolean);

  const fallbackButtons = () => {
    if (page.layoutStyle === "horizontal" || page.layoutStyle === "vertical") {
      return [
        mapButtonToFlex({ text: "Button 1" }, { ...options, layoutStyle: page.layoutStyle }),
        mapButtonToFlex({ text: "Button 2" }, { ...options, layoutStyle: page.layoutStyle }),
      ];
    }
    return [mapButtonToFlex({ text: "Button" }, { ...options, layoutStyle: page.layoutStyle })];
  };

  const btnsWithFallback = btnsRaw.length ? btnsRaw : fallbackButtons();

  const bodyContents = [
    {
      type: "text",
      text: page.textProps?.text || " ",
      weight: "bold",
      size: page.textProps?.fontSize ? `${page.textProps.fontSize}px` : "xl",
      color: page.textProps?.color || "#000000",
      align: "center",
      wrap: true,
      margin: "none",
    },
  ];

  const limitedBtns =
    page.layoutStyle === "standard"
      ? btnsWithFallback.slice(0, 1)
      : page.layoutStyle === "horizontal" || page.layoutStyle === "vertical"
      ? btnsWithFallback.slice(0, 2)
      : btnsWithFallback;

  const layoutMap = {
    standard: "vertical",
    vertical: "vertical",
    horizontal: "horizontal",
  };

  const heroAction =
    limitedBtns[0]?.action && limitedBtns[0].action.type === "uri"
      ? limitedBtns[0].action
      : undefined;

  if (limitedBtns.length) {
    bodyContents.push({
      type: "box",
      layout: layoutMap[page.layoutStyle] || "vertical",
      spacing: "sm",
      margin: "md",
      contents: limitedBtns,
    });
  }

  return {
    type: "bubble",
    //size: "regular", // 明確指定使用最大寬度
    ...(hasValidImage
      ? {
          hero: {
            type: "image",
            url: imageUrl,
            size: "full",
            aspectRatio,
            aspectMode: "fit",
            ...(heroAction ? { action: heroAction } : {}),
          },
        }
      : {}),
    body: {
      type: "box",
      layout: "vertical",
      paddingAll: "10px",
      spacing: "xs",
      backgroundColor: page.canvasBg || "#ffffff",
      contents: bodyContents,
    },
    styles: { footer: { separator: true } },
  };
};

export const buildFlexMessageFromPages = ({
  pages = [],
  altText = "Flex Message",
  allowLocalImage = false,
  includePreviewMeta = false,
} = {}) => {
  const bubbles = (pages || []).map((p) =>
    buildFlexBubbleFromPage(p, { allowLocalImage, includePreviewMeta })
  );
  const message = createFlexMessage({
    altText,
    bubbles: bubbles.length ? bubbles : undefined,
  });
  return normalizeMessage(message);
};

export const normalizeMessages = (messages = []) =>
  (messages || []).map((m) => normalizeMessage(m)).filter(Boolean);

// 組合最終出站 payload，對應後端 /api/send-line
export const buildOutboundPayload = ({
  messages = [],
  token = "",
  userId = "",
  sendMode = "push",
}) => {
  const normalized = normalizeMessages(messages);
  return {
    lineToken: token,
    lineUserId: userId,
    sendMode,
    messages: normalized,
  };
};

// 針對模板/圖片等，做最小必需驗證；回傳錯誤訊息陣列
export const validateMessages = (messages = []) => {
  const errors = [];
  messages.forEach((msg, idx) => {
    if (msg.type === LINE_MESSAGE_TYPES.TEXT) {
      if (!validators.required(msg.text)) {
        errors.push(`第 ${idx + 1} 則文字訊息缺少 text`);
      }
    }
    if (msg.type === LINE_MESSAGE_TYPES.IMAGE) {
      if (!validators.isHttpsUrl(msg.originalContentUrl)) {
        errors.push(`第 ${idx + 1} 則圖片 originalContentUrl 需為 https URL`);
      }
      if (
        msg.previewImageUrl &&
        !validators.isHttpsUrl(msg.previewImageUrl)
      ) {
        errors.push(`第 ${idx + 1} 則圖片 previewImageUrl 需為 https URL`);
      }
    }
    if (msg.type === LINE_MESSAGE_TYPES.TEMPLATE) {
      const tpl = msg.template || {};
      if (!validators.required(msg.altText)) {
        errors.push(`第 ${idx + 1} 則 template 需 altText`);
      }
      if (!validators.required(tpl.text)) {
        errors.push(`第 ${idx + 1} 則 template 需 text`);
      }
    }
    if (msg.type === LINE_MESSAGE_TYPES.FLEX) {
      if (!validators.required(msg.altText)) {
        errors.push(`第 ${idx + 1} 則 flex 需 altText`);
      }
    }
  });
  return errors;
};
